/**
 * Floor Renderer
 *
 * Creates Three.js geometry for floor surfaces.
 * Supports rectangular floors with configurable colors.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import { createFloorMaterial, applyShadowSettings } from '../utils/materials'

/**
 * Floor rendering options
 */
export interface FloorRenderOptions {
  color?: string
  enableShadows?: boolean
  yOffset?: number  // feet above ground (default: 0.01 to avoid z-fighting)
}

/**
 * Floor dimensions
 */
export interface FloorDimensions {
  width: number   // feet (X-axis)
  depth: number   // feet (Z-axis / Y in 2D)
}

/**
 * Render a rectangular floor
 */
export function renderFloor(
  dimensions: FloorDimensions,
  options: FloorRenderOptions = {}
): THREE.Mesh {
  const {
    color,
    enableShadows = true,
    yOffset = 0.01,
  } = options

  const { width, depth } = dimensions

  // Create floor geometry (plane)
  const geometry = new THREE.PlaneGeometry(
    width * FEET_TO_METERS,
    depth * FEET_TO_METERS
  )

  // Create floor material
  const material = createFloorMaterial({ color })

  // Create mesh
  const floor = new THREE.Mesh(geometry, material)

  // Rotate to be horizontal (facing up)
  floor.rotation.x = -Math.PI / 2

  // Position slightly above ground to avoid z-fighting
  floor.position.y = yOffset * FEET_TO_METERS

  // Apply shadow settings (floors receive but don't cast)
  applyShadowSettings(floor, enableShadows, false)

  floor.name = 'floor'

  return floor
}

/**
 * Create a unit floor at a specific offset
 */
export function renderUnitFloor(
  dimensions: FloorDimensions,
  offset: { x: number; y: number },  // Offset from building origin in feet
  options: FloorRenderOptions = {}
): THREE.Mesh {
  const floor = renderFloor(dimensions, options)

  // Apply offset (convert from feet to meters)
  floor.position.x = offset.x * FEET_TO_METERS
  floor.position.z = -offset.y * FEET_TO_METERS  // Negate: DB +Y = north, scene -Z = north (scene +Z = south)

  return floor
}
